import { useState } from "react";
import {
  BooleanInput,
  Button,
  Edit,
  ReferenceInput,
  required,
  SimpleForm,
  useEditContext,
  useGetOne,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
} from "react-admin";
import { useFormState, useWatch } from "react-hook-form";
import { Box, Typography } from "@mui/material";
import { dormitoryRequests } from "api/dormitoryApi";
import dataProvider from "server/dataProvider";
import { DormitoryRoomStatuses, DormitoryServicePath, ROOMS } from "utils/constants";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import StatusToggleDialog from "../Partials/StatusToggleDialog";
import { useNotifyError } from "utils/useNotifyError";
import DormitoryAutocompleteInput from "../Partials/DormitoryAutocompleteInput";
import SaveForm from "./SaveForm";
import BackButtonToolbar from "features/BackButtonToolbar";
import InactiveDeleteOptionEditToolbar from "features/EditToolbar";

const roomResource = `${DormitoryServicePath}${ROOMS}`;
const roomFields = ["roomNumber", "floor", "buildingId", "typeId", "exposition"];

const ConditionControl = () => {
  const automatic = useWatch({ name: "automaticCondition" });
  const record = useRecordContext();

  return (
    <Box>
      {automatic ? (
        <DormitoryAutocompleteInput
          source="automaticConditionId"
          label="Състояние"
          choices={[{
            id: "automatic",
            name: record.automaticCondition ? record.conditionDescription : "Определя се автоматично",
          }]}
          disabled
          fullWidth
          helperText={false}
        />
      ) : (
        <ReferenceInput
          reference={dormitoryRequests.getRoomConditionsSelect()}
          source="manualConditionId"
          filter={{ manualOnly: true }}
        >
          <DormitoryAutocompleteInput
            label="Състояние"
            fullWidth
            validate={[required()]}
            optionText="name"
            helperText={false}
          />
        </ReferenceInput>
      )}
      <BooleanInput source="automaticCondition" label="Автоматично" helperText={false} />
    </Box>
  );
};

const RoomAvailability = () => {
  const record = useRecordContext();
  const { isSubmitting } = useFormState();
  const values = useWatch();
  const hasUnsavedChanges = roomFields.some(field => String(values[field]) !== String(record[field]))
    || values.automaticCondition !== record.automaticCondition
    || (!values.automaticCondition && String(values.manualConditionId) !== String(record.manualConditionId));
  const isActive = record?.status === DormitoryRoomStatuses.Active;

  return (
    <Box sx={{ width: "100%", textAlign: "center", mb: 2 }}>
      <Typography sx={{ mb: 1 }}>Статус: {isActive ? "Активна" : "Неактивна"}</Typography>
      <SecuredComponent permission={permissions.canManageDormitoryRooms}>
        <Box>
          <StatusToggleDialog
            disabled={hasUnsavedChanges || isSubmitting}
            isActive={isActive}
            activateUrl={dormitoryRequests.activateRoom(record.id)}
            deactivateUrl={dormitoryRequests.deactivateRoom(record.id)}
            confirmMessage="Сигурни ли сте, че искате да смените статуса на стаята?"
            successMessage="Успешно сменихте статуса на стаята"
          />
        </Box>
        {hasUnsavedChanges && (
          <Typography variant="caption">Запазете промените, преди да смените статуса.</Typography>
        )}
      </SecuredComponent>
    </Box>
  );
};

const RoomEditForm = () => {
  const { record } = useEditContext();
  const [saving, setSaving] = useState(false);
  const notify = useNotify();
  const notifyError = useNotifyError();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const { data: condition, isPending, error, refetch } = useGetOne(
    `${DormitoryServicePath}room-conditions`,
    { id: record?.conditionId },
    { enabled: record?.conditionId != null },
  );

  if (!record || isPending) return <Typography sx={{ p: 2 }}>Зареждане ...</Typography>;
  if (error || !condition) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Състоянието на стаята не можа да се зареди.</Typography>
        <Button label="Опитай отново" onClick={() => refetch()} />
      </Box>
    );
  }

  const automatic = condition.occupancyKind != null;
  const formRecord = {
    ...record,
    automaticCondition: automatic,
    automaticConditionId: "automatic",
    manualConditionId: automatic ? null : record.conditionId,
  };

  const save = async values => {
    if (saving) return;
    setSaving(true);
    let roomSaved = false;
    try {
      const changedRoom = roomFields.some(field => String(values[field]) !== String(record[field]));
      const changedCondition = values.automaticCondition !== automatic
        || (!values.automaticCondition && String(values.manualConditionId) !== String(record.conditionId));

      if (changedRoom) {
        const data = Object.fromEntries(roomFields.map(field => [field, values[field]]));
        await dataProvider.update(roomResource, { id: record.id, data, previousData: record });
        roomSaved = true;
      }
      if (changedCondition) {
        await dataProvider.customRequest(
          values.automaticCondition
            ? dormitoryRequests.resetRoomCondition(record.id)
            : dormitoryRequests.setRoomCondition(record.id),
          values.automaticCondition ? null : { conditionId: values.manualConditionId },
          "PUT",
        );
      }
      notify("Промените са записани", { type: "success" });
      refresh();
      redirect("list", roomResource);
    } catch (err) {
      notifyError(err);
      if (roomSaved) {
        notify("Данните за стаята са записани, но състоянието не е променено. Опитайте отново.", {
          type: "warning",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <SimpleForm
      record={formRecord}
      onSubmit={save}
      toolbar={<InactiveDeleteOptionEditToolbar disabledSaveButton={saving} />}
    >
      <RoomAvailability />
      <Box component="fieldset" disabled={saving} sx={{ border: 0, p: 0, m: 0, minWidth: 0, width: "100%" }}>
        <SaveForm conditionControl={<ConditionControl />} />
      </Box>
    </SimpleForm>
  );
};

const EditRoom = () => (
  <Edit
    title="Редактиране на стая"
    mutationMode="pessimistic"
    actions={<BackButtonToolbar />}
  >
    <RoomEditForm />
  </Edit>
);

export default EditRoom;
